import { FieldDef } from 'app/core/models';
import { GraphqlFilterSort, GraphqlParams } from 'app/shared/models/graphql';

export class GraphqlParamsBuilder {
  private connection: string;
  private pagination = { page: 1, size: 20 };
  private fields: string[] = [];
  private fjson = '[]';
  private query = '';
  private fieldDefs: FieldDef[] = [];
  private group?: string;
  private options?: any;
  private startDate?: string;
  private endDate?: string;
  private cadence?: string;
  private groupBy?: any[];
  private excludeSubtotals?: boolean;
  private processNodeFields?: boolean;
  private normalizeMethod?: (fields: string[]) => any;
  private filterDictionary?: any;
  private sort?: GraphqlFilterSort[];
  private location?: string;
  private pipelineId?: string;
  private tags?: string[];

  constructor(connection: string) {
    this.connection = connection;
  }

  /**
   * Set pagination parameters
   */
  withPagination(page: number, size: number): GraphqlParamsBuilder {
    this.pagination = { page, size };
    return this;
  }

  /**
   * Set fields to retrieve
   */
  withFields(fields: string[]): GraphqlParamsBuilder {
    this.fields = fields;
    return this;
  }

  /**
   * Add a single field
   */
  addField(field: string): GraphqlParamsBuilder {
    this.fields.push(field);
    return this;
  }

  /**
   * Set filter JSON
   */
  withFilterJson(fjson: Record<string, any> | string): GraphqlParamsBuilder {
    if (typeof fjson === 'string') {
      this.fjson = fjson;
    } else {
      this.fjson = JSON.stringify(fjson);
    }
    return this;
  }

  /**
   * Set search query
   */
  withQuery(query: string): GraphqlParamsBuilder {
    this.query = query;
    return this;
  }

  /**
   * Set field definitions
   */
  withFieldDefs(fieldDefs: FieldDef[]): GraphqlParamsBuilder {
    this.fieldDefs = fieldDefs;
    return this;
  }

  /**
   * Set group parameter
   */
  withGroup(group: string): GraphqlParamsBuilder {
    this.group = group;
    return this;
  }

  /**
   * Set options
   */
  withOptions(options: any): GraphqlParamsBuilder {
    this.options = options;
    return this;
  }

  /**
   * Set date range
   */
  withDateRange(startDate: string, endDate: string): GraphqlParamsBuilder {
    this.startDate = startDate;
    this.endDate = endDate;
    return this;
  }

  /**
   * Set start date
   */
  withStartDate(startDate: string): GraphqlParamsBuilder {
    this.startDate = startDate;
    return this;
  }

  /**
   * Set end date
   */
  withEndDate(endDate: string): GraphqlParamsBuilder {
    this.endDate = endDate;
    return this;
  }

  /**
   * Set cadence
   */
  withCadence(cadence: string): GraphqlParamsBuilder {
    this.cadence = cadence;
    return this;
  }

  /**
   * Set group by fields
   */
  withGroupBy(groupBy: any[]): GraphqlParamsBuilder {
    this.groupBy = groupBy;
    return this;
  }

  /**
   * Set exclude subtotals flag
   */
  withExcludeSubtotals(excludeSubtotals: boolean): GraphqlParamsBuilder {
    this.excludeSubtotals = excludeSubtotals;
    return this;
  }

  /**
   * Set process node fields flag
   */
  withProcessNodeFields(processNodeFields: boolean): GraphqlParamsBuilder {
    this.processNodeFields = processNodeFields;
    return this;
  }

  /**
   * Set normalize method
   */
  withNormalizeMethod(normalizeMethod: (fields: string[]) => any): GraphqlParamsBuilder {
    this.normalizeMethod = normalizeMethod;
    return this;
  }

  /**
   * Set filter dictionary
   */
  withFilterDictionary(filterDictionary: any): GraphqlParamsBuilder {
    this.filterDictionary = filterDictionary;
    return this;
  }

  /**
   * Set sort parameter
   */
  withSort(sort: GraphqlFilterSort[]): GraphqlParamsBuilder {
    this.sort = sort;
    return this;
  }

  /**
   * Set location filter
   */
  withLocation(location: string): GraphqlParamsBuilder {
    this.location = location;
    return this;
  }

  /**
   * Set pipeline ID filter
   */
  withPipelineId(pipelineId: string): GraphqlParamsBuilder {
    this.pipelineId = pipelineId;
    return this;
  }

  /**
   * Set tags filter
   */
  withTags(tags: string[]): GraphqlParamsBuilder {
    this.tags = tags;
    return this;
  }

  /**
   * Build the GraphqlParams object
   */
  build(): GraphqlParams {
    const filter: any = {
      query: this.query,
      fjson: this.fjson,
      pagination: this.pagination,
    };

    if (this.filterDictionary) {
      filter.filterDictionary = this.filterDictionary;
    }

    if (this.sort) {
      filter.sort = this.sort;
    }

    if (this.location) {
      filter.location = this.location;
    }

    if (this.pipelineId) {
      filter.pipeline_id = this.pipelineId;
    }

    if (this.tags) {
      filter.tags = this.tags;
    }

    return {
      connection: this.connection,
      fields: this.fields,
      filter,
      fieldDefs: this.fieldDefs,
      ...(this.group && { group: this.group }),
      ...(this.options && { options: this.options }),
      ...(this.startDate && { startDate: this.startDate }),
      ...(this.endDate && { endDate: this.endDate }),
      ...(this.cadence && { cadence: this.cadence }),
      ...(this.groupBy && { groupBy: this.groupBy }),
      ...(this.excludeSubtotals && { excludeSubtotals: this.excludeSubtotals }),
      ...(this.processNodeFields && { processNodeFields: this.processNodeFields }),
      ...(this.normalizeMethod && { normalizeMethod: this.normalizeMethod }),
    };
  }

  /**
   * Create a new builder instance
   */
  static forConnection(connection: string): GraphqlParamsBuilder {
    return new GraphqlParamsBuilder(connection);
  }
}
